/**
 * Paste into the app chat's composer (mission stream contract phase 4,
 * agion/Agion#2327). @agion/mission-stream decides what a paste becomes
 * (paste.ts); this hook applies it to the composer:
 *
 * - spreadsheet cells go in as a Markdown table, never as HTML;
 * - files go to the chat's upload hook, whose limits apply;
 * - plain text is pasted by the browser;
 * - a paste that would pass MESSAGE_TEXT_MAX_BYTES, or files where the chat
 *   takes none, is refused, and pasteRefusal says why until the text changes.
 */

import { useCallback, useEffect, useState, type ClipboardEvent } from 'react'
import { clipboardContent, pasteOutcome, readPaste, unknownKind, type PasteRefusal } from '@agion/mission-stream'

interface UseComposerPasteOptions {
  /** The composer's text. */
  value: string
  setValue: (value: string) => void
  /** Whether the composer takes attachments (the mission has a host). */
  canAttach: boolean
  addFiles: (files: File[]) => void
}

export interface ComposerPaste {
  onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  /** Why the last paste was not taken, until the text changes. */
  pasteRefusal: PasteRefusal | null
}

function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(html, 'text/html')
}

export function useComposerPaste({ value, setValue, canAttach, addFiles }: UseComposerPasteOptions): ComposerPaste {
  const [pasteRefusal, setPasteRefusal] = useState<PasteRefusal | null>(null)

  // A refusal speaks about the text it refused to change; new text ends it.
  useEffect(() => {
    setPasteRefusal(null)
  }, [value])

  const onPaste = useCallback((event: ClipboardEvent<HTMLTextAreaElement>) => {
    const clip = clipboardContent(event.clipboardData)
    const box = event.currentTarget
    const outcome = pasteOutcome(readPaste(clip, parseHtml), {
      value,
      start: box.selectionStart ?? value.length,
      end: box.selectionEnd ?? value.length,
      plainText: clip.text,
      canAttach,
    })
    switch (outcome.kind) {
      case 'insert':
        event.preventDefault()
        setValue(outcome.value)
        return
      case 'attach':
        event.preventDefault()
        setPasteRefusal(null)
        addFiles(outcome.files)
        return
      case 'browser':
        return
      case 'refuse':
        event.preventDefault()
        setPasteRefusal(outcome.reason)
        return
      default:
        unknownKind(outcome, undefined)
    }
  }, [value, setValue, canAttach, addFiles])

  return { onPaste, pasteRefusal }
}
